import type { TCitationElement } from '@alpod-ai-writer/plate-plugins/citations';
import { CitationPlugin } from '@alpod-ai-writer/plate-plugins/citations/react';
import { DocumentSectionType } from '@alpod-ai-writer/shared';
import { cn,withRef } from '@udecode/cn';
import { IS_APPLE } from '@udecode/plate';
import { PlateElement, useEditorPlugin, useFocused, useReadOnly,useSelected } from '@udecode/plate/react';
import React, { useCallback } from 'react';

import { useMounted } from '../../../hooks/useMounted.hook';
import { useCitationList } from '../../hooks/useCitationList.hook';
import { useDocumentEditor } from '../../hooks/useDocumentEditor.hook';
import { useSectionNav } from '../../hooks/useSectionNav.hook';

interface CitationElementProps {
    prefix?: string;
    onClick?: (mentionNode: any) => void;
}

export const CitationElement = withRef<typeof PlateElement, CitationElementProps>(({
    children,
    className,
    prefix,
    ...props
}, ref) => {
    const editor = useDocumentEditor();
    const plugin = useEditorPlugin(CitationPlugin);
    const { citationMap } = useCitationList();
    const { selectSectionByType } = useSectionNav(editor.template.config.sections);

    const element = props.element as TCitationElement;
    const selected = useSelected();
    const focused = useFocused();
    const mounted = useMounted();
    const readOnly = useReadOnly();

    const handleEditCitation = useCallback(() => {
        if (element.ref) {
            plugin.api.floatingCitation.hide();
            selectSectionByType(DocumentSectionType.CitationList, element.ref);
        }
    }, [ selectSectionByType, element.ref ]);

    const handleOnClick = useCallback(() => {
        if (element.ref) {
            plugin.api.floatingCitation.show(plugin.editor.id, element, handleEditCitation);
        }
    }, [ selectSectionByType, element.ref, element.pageNumber ]);

    const label = citationMap.get(element.ref)?.userLabel ?? `(citace byla smazána @${element.ref})`;
    const pageNumberPrefix = plugin.getOption('pageNumberPrefix');

    return (
        <PlateElement
            as={'span'}
            ref={ref}
            className={cn(
                className,
                'inline-block rounded-md bg-gray-200 hover:bg-blue-200 px-1.5 py-0.5 align-baseline text-sm font-medium transition-colors duration-200 group',
                !readOnly && 'cursor-pointer',
                selected && focused && 'ring-2 ring-ring',
                element.children[0].bold === true && 'font-bold',
                element.children[0].italic === true && 'italic',
                element.children[0].underline === true && 'underline'
            )}
            onClick={handleOnClick}
            data-slate-value={element.ref}
            contentEditable={false}
            draggable
            {...props}
        >
            {mounted && IS_APPLE ? (
                // Mac OS IME https://github.com/ianstormtaylor/slate/issues/3490
                <React.Fragment>
                    {children}
                    {prefix}
                    {label}
                </React.Fragment>
            ) : (
                // Others like Android https://github.com/ianstormtaylor/slate/pull/5360
                <React.Fragment>
                    {prefix}
                    {label}
                    {children}
                </React.Fragment>
            )}
            {element.pageNumber && (
                <>
                    <span className='border-l border-gray-300 pl-2 ml-2 group-hover:border-blue-300 transition-colors duration-200'>{pageNumberPrefix} {element.pageNumber}</span>
                </>
            )}
        </PlateElement>
    );
});
